import Axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { camelizeKeys } from 'humps';
import i18next from 'i18next';
import { get } from 'lodash';
import React, { createContext } from 'react';
import { handleFetchSocketSuccess, handleFetchSocketToken } from '../hooks/socket/useSocketSessionMutation';
import StorageHelper from '../utils/StorageHelper';
import { configs } from './configs';

const AxiosContext = createContext<AxiosInstance>(Axios);
const AxiosSocketContext = createContext<AxiosInstance>(Axios);

export const AxiosProvider = ({ children }: React.PropsWithChildren<unknown>) => {
  const axios = React.useMemo(() => {
    const axios = Axios.create({
      baseURL: configs.baseURL,
      timeout: 60000,
      headers: {
        'Content-Type': 'application/json',
        'accept-language': get(i18next, 'language', 'en'),
        accept: 'application/json',
      },
    });
    axios.interceptors.request.use(async (config: AxiosRequestConfig) => {
      // Read token for anywhere, in this case directly from localStorage
      // const token = localStorage.getItem('token');
      // if (token) {
      //   config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
      // }
      const { token } = await StorageHelper.getToken();
      if (token) {
        config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
      }
      // config.headers = {
      //   ...config.headers,
      //   Authorization: `Bearer eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiIsImp0aSI6ImU4MjJjM2UwYTM0OTY2ODAwOGUzMjUzZjRkNGMyYzBjZTA2MDM5ZTFlYjQ1N2NkNjhhMDU2NWFiY2RiNjAwODUwOGVhZjgyN2Y4YmFjYWU4In0.eyJhdWQiOiIyIiwianRpIjoiZTgyMmMzZTBhMzQ5NjY4MDA4ZTMyNTNmNGQ0YzJjMGNlMDYwMzllMWViNDU3Y2Q2OGEwNTY1YWJjZGI2MDA4NTA4ZWFmODI3ZjhiYWNhZTgiLCJpYXQiOjE2NzQ4OTEyOTcsIm5iZiI6MTY3NDg5MTI5NywiZXhwIjoxNjc2MTg3Mjk3LCJzdWIiOiIxMjEzNzkiLCJzY29wZXMiOltdLCJpZCI6MTIxMzc5LCJwcm9pdmVyIjoidXNlcnMiLCJvcmlnaW4iOiJodHRwczpcL1wvZ29kZXYubmhhbTI0LmNvbSJ9.vKpKBiij1KzLjEX3vde5IO0eyW301179LUNY9bJI1hNq_50PRL7gvrlhOpATR2mMWsoEwesGUx2bmSanOOA2o9VPaGpDXq9ZSViUIhXs4hwJ_2gzVVuf-JZ7yePQakSE87knvSFuxs_6DbQZA2GKv3l7wZVriGsqF_mSu3tK80eQAUxoRMSKVYs9zhgh5jum9duJrVvMoWcNMH4aTuDADwYGgTYzvBgTXqi6wqmVndalqNG7YLTkMV-3sOtHTMDSyBnPnMm662x9bvJe2OgBy_Hxae8ElPy-o6dnaTn4QcYMNnE4Nwnz1hRWULeLUD3Qkxef_mZlvaPQduHxIVLftXtlwgmp4bz9-2O2-2v9M5bGGWpEXggTF7sCB6rRg5QbO1VbNNdN5kHm8PKSnLa-k-Ew62_ksT1CF3T1q5MBo_LE1rHuVcJFMI2ik-70iPcKGe95HEqKEiJfcvA9Dua1zqzzLsGWfF6MOY0AfE-1vVYhQJq9wC5pvSeyfPF6UQau3pzzZCR-En_nuG8gHisGFI_K2NyiIptFa9fEnzkURkCAhigqTjN-00EBj8F9DGu8bNtfDYSYlxQMVNMimcMfqP3X53giuTFrjgS7VOjXJ02CVL6Pnt28_vYte8EnQk-IAsq3Zo_7woyuP_BrXxlOYFnihlXR2msQp6kwiN6t0Po`,
      // };

      return config;
    });

    axios.interceptors.response.use(
      (response: AxiosResponse<any>) => {
        console.log(`${response.config.url}`, response);
        const finalResponse = { ...response, data: camelizeKeys(response.data) };
        return finalResponse;
      },
      error => {
        console.log(`${error.config.url}`, error);

        return Promise.reject(error);
      },
    );

    return axios;
  }, []);

  const axiosSocket = React.useMemo(() => {
    const axiosSocket = Axios.create({
      baseURL: configs.socketBaseURL,
      timeout: 60000,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'accept-language': get(i18next, 'language', 'en'),
      },
    });

    axiosSocket.interceptors.request.use(async (config: AxiosRequestConfig) => {
      // Read token for anywhere, in this case directly from localStorage
      const token = await StorageHelper.getSocketToken();
      if (token) {
        config.headers = { ...config.headers, Authorization: `${token}` };
      }
      // config.headers = {
      //   ...config.headers,
      //   'Content-Type': 'application/json',
      // };

      return config;
    });

    axiosSocket.interceptors.response.use(
      (response: AxiosResponse<any>) => {
        console.log(`${response.config.url}`, response);
        const finalResponse = { ...response, data: camelizeKeys(response.data) };
        return finalResponse;
      },
      (error: AxiosError) => {
        console.log(`${error.config.url}`, error);
        if (error?.response?.status === 401) {
          handleFetchSocketToken(axiosSocket)
            .then(handleFetchSocketSuccess)
            .catch(() => {});
        }
        return Promise.reject(error);
      },
    );

    return axiosSocket;
  }, []);

  return (
    <AxiosSocketContext.Provider value={axiosSocket}>
      <AxiosContext.Provider value={axios}>{children}</AxiosContext.Provider>
    </AxiosSocketContext.Provider>
  );
};

export function useAxios() {
  return React.useContext(AxiosContext);
}
export function useAxiosSocket() {
  return React.useContext(AxiosSocketContext);
}
